/**
 * X3DH (Extended Triple Diffie-Hellman) — ASYNCHRONOUS session establishment.
 *
 * It allows Alice to start a session with Bob even though Bob is offline: Bob has
 * previously published a {@link PreKeyBundle}; Alice does 3-4 ECDH against it and
 * derives a shared secret `SK` that seeds the double ratchet. In her first
 * message Alice attaches the public keys (IK_A, EK_A) and which prekeys she used,
 * so that Bob recomputes the SAME `SK` when he receives it.
 *
 * DH1 = DH(IK_A, SPK_B)   authenticates Alice against Bob's signed prekey
 * DH2 = DH(EK_A, IK_B)    authenticates Bob and contributes Alice's ephemeral
 * DH3 = DH(EK_A, SPK_B)
 * DH4 = DH(EK_A, OPK_B)   (if there is a one-time prekey; gives extra forward secrecy)
 * SK  = HKDF( DH1 || DH2 || DH3 || DH4 )
 *
 * The "associated data" (AD) that will be used as AAD in the ratchet ties the session
 * to BOTH identities: move a message to another conversation and the open fails.
 */

import { concatBytes } from "./crypto/encoding";
import { dh, generateDhKeyPair } from "./crypto/dh";
import { hkdfExpand } from "./crypto/primitives";
import { type DeviceIdentity, type PreKeyBundle, publishBundle, verifyBundle } from "./identity";

const SK_INFO = "susurro:x3dh:sk:v1";

/** Header that Alice sends in her first message so Bob recomputes SK. */
export interface X3dhInitHeader {
  readonly identityDhPub: Uint8Array;
  readonly ephemeralPub: Uint8Array;
  readonly usedSignedPreKeyId: string;
  readonly usedOneTimePreKeyId?: string;
}

export interface InitiatorResult {
  readonly sharedSecret: Uint8Array;
  readonly associatedData: Uint8Array;
  readonly header: X3dhInitHeader;
  /** Bob's signed prekey DH public key: first remote key of the ratchet. */
  readonly peerSignedPreKeyPub: Uint8Array;
}

/** Alice's side: derives SK from Bob's bundle. Verifies the SPK signature. */
export async function initiateSession(
  self: DeviceIdentity,
  bundle: PreKeyBundle,
): Promise<InitiatorResult> {
  if (!(await verifyBundle(bundle))) {
    throw new Error("x3dh: signed prekey signature invalid (posible MITM)");
  }
  const ephemeral = await generateDhKeyPair();

  const dh1 = await dh(self.identityDh.privateKey, bundle.signedPreKeyPub);
  const dh2 = await dh(ephemeral.privateKey, bundle.identityDhPub);
  const dh3 = await dh(ephemeral.privateKey, bundle.signedPreKeyPub);
  const parts = [dh1, dh2, dh3];
  if (bundle.oneTimePreKeyPub) {
    parts.push(await dh(ephemeral.privateKey, bundle.oneTimePreKeyPub));
  }
  const sharedSecret = await hkdfExpand(concatBytes(...parts), SK_INFO);

  return {
    sharedSecret,
    associatedData: concatBytes(self.identityDh.publicKey, bundle.identityDhPub),
    header: {
      identityDhPub: self.identityDh.publicKey,
      ephemeralPub: ephemeral.publicKey,
      usedSignedPreKeyId: bundle.signedPreKeyId,
      usedOneTimePreKeyId: bundle.oneTimePreKeyId,
    },
    peerSignedPreKeyPub: bundle.signedPreKeyPub,
  };
}

export interface ResponderResult {
  readonly sharedSecret: Uint8Array;
  readonly associatedData: Uint8Array;
}

/**
 * Bob's side: recomputes SK with the local private keys and Alice's
 * header. Consumes the indicated one-time prekey (single use). The private SPK is
 * passed explicitly to tolerate rotation (Bob keeps recent SPKs by id).
 */
export async function respondSession(
  self: DeviceIdentity,
  header: X3dhInitHeader,
): Promise<ResponderResult> {
  const spk = self.signedPreKey;
  if (spk.keyId !== header.usedSignedPreKeyId) {
    throw new Error("x3dh: signed prekey desconocido (rotado/expirado)");
  }
  const dh1 = await dh(spk.keyPair.privateKey, header.identityDhPub);
  const dh2 = await dh(self.identityDh.privateKey, header.ephemeralPub);
  const dh3 = await dh(spk.keyPair.privateKey, header.ephemeralPub);
  const parts = [dh1, dh2, dh3];
  if (header.usedOneTimePreKeyId) {
    const opk = self.oneTimePreKeys.get(header.usedOneTimePreKeyId);
    if (!opk) {
      throw new Error("x3dh: one-time prekey already consumed or missing");
    }
    self.oneTimePreKeys.delete(header.usedOneTimePreKeyId); // single use
    parts.push(await dh(opk.privateKey, header.ephemeralPub));
  }
  const sharedSecret = await hkdfExpand(concatBytes(...parts), SK_INFO);
  return {
    sharedSecret,
    associatedData: concatBytes(header.identityDhPub, self.identityDh.publicKey),
  };
}

/** Sugar: get a bundle "from the directory" for a peer (consumes an OPK). */
export function fetchBundle(peer: DeviceIdentity): PreKeyBundle {
  return publishBundle(peer);
}
